<template lang="html">
  <div
    v-if="isSelect"
    class="setting-select-wrapper"
  >
    <span class="UI-icon UI-expand" />
    <select
      :id="name"
      v-model="value"
      :class="settingInputClass"
    >
      <option
        v-for="(val, i) in defaultValue"
        :key="val"
        :value="hasValuesAssociated ? valuesAssociated[i] : val"
      >
        <span v-if="valuei18nkeys.length > 0">{{ $t(`templates.${valuei18nkeys[i]}`) }}</span>
        <span v-else>{{ val }}</span>
      </option>
    </select>
  </div>

  <textarea
    v-else-if="isTextArea"
    :id="name"
    v-model="value"
    :focus="focus"
    :class="settingInputClass"
    @focus="removePlaceholder()"
    @focusout="resetPlaceholder()"
  />
  <div
    v-else-if="isText || isUrl || isCheckbox || isNumber"
    :class="'setting-input-wrapper_' + type.toLowerCase()"
    class="setting-input-wrapper"
  >
    <input
      :id="name"
      v-model="value"
      :type="type.toLowerCase()"
      :autofocus="focus"
      :placeholder="placeholder"
      :required="required"
      :class="settingInputClass"
      :maxlength="maxLength"
      :step="isNumber ? 'any' : null"
      @input="inputChanged"
    >
    <button
      v-if="!isCheckbox && !isNumber"
      type="button"
      @click="clearValue()"
    >
      <span class="UI-icon UI-delete" />
    </button>
  </div>

  <setting-geocode
    v-else-if="isGeocode"
    :focus="focus"
  />
</template>

<script>
import SettingGeocode from './SettingGeocode'
import { SELECT, TEXT, TEXTAREA, NUMBER, URL, GEOCODE, CHECKBOX } from '../config'
import bus from '../bus'
import Levenshtein from 'levenshtein'

export default {
  name: 'SettingInput',

  components: {
    SettingGeocode
  },

  props: {
    name: { type: String, required: true },
    type: { type: String, default: null },
    defaultValue: { type: [String, Number, Array, Boolean], default: null },
    placeholder: { type: String, default: null },
    focus: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    validation: { type: Function, default: null },
    i18nvalue: { type: String, default: null },
    locale: { type: String, default: null },
    maxLength: { type: Number, default: null },
    valuei18nkeys: { type: Array, default: () => [] },
    valuesAssociated: { type: Array, default: () => [] }
  },

  data () {
    return {
      value: '',
      defValue: '',
      highlight: false,
      setToRtlTextArea: false
    }
  },
  computed: {
    settingInputClass () {
      return {
        'setting-input': true,
        'is-placeholder': this.isPlaceholder,
        highlight: this.highlight,
        rtl: this.setToRtlTextArea
      }
    },

    formattedValue () {
      return this.type === NUMBER ? parseFloat(this.value) : this.value
    },

    hasValuesAssociated () {
      return this.valuesAssociated.length > 0
    },

    isSelect () {
      return this.type === SELECT
    },

    isCheckbox () {
      return this.type === CHECKBOX
    },

    isText () {
      return this.type === TEXT
    },

    isNumber () {
      return this.type === NUMBER
    },

    isTextArea () {
      return this.type === TEXTAREA
    },

    isUrl () {
      return this.type === URL
    },

    isGeocode () {
      return this.type === GEOCODE
    },

    isPlaceholder () {
      return this.value === this.defValue
    }
  },

  watch: {
    value (newValue, oldValue) {
      newValue = this.type === NUMBER ? parseFloat(newValue) : newValue
      newValue = this.type === String ? newValue.toLowerCase() : newValue
      newValue = this.validation !== null ? this.validation(newValue) : newValue
      if (this.highlight && (newValue.length > 0 || newValue !== null)) {
        this.highlight = false
      }
      if (this.required && this.type === 'url' && oldValue.length > 0) {
        const l = new Levenshtein(oldValue, newValue)
        if (l.distance > 1) {
          bus.$emit('reset-config')
        }
      }
      this.$emit('input', newValue)
    },

    defaultValue () {
      this.setDefaultValue()
    }
  },

  created () {
    this.setDefaultValue()
  },

  mounted () {
    this.addEventListeners()
  },

  beforeDestroyed () {
    this.removeEventListeners()
  },

  methods: {
    setDefaultValue () {
      if (this.isTextArea && this.i18nvalue) {
        this.defValue = this.$t(`templates.${this.i18nvalue}`)
      } else {
        this.defValue = this.defaultValue
      }
      this.value = this.isSelect ? this.hasValuesAssociated ? this.valuesAssociated[0] : this.defValue[0] : this.defValue
      this.$emit('input', this.value)
    },

    addEventListeners () {
      bus.$on('highlight-errors', this.highlightErrors)
    },

    removeEventListeners () {
      bus.$off('highlight-errors', this.highlightErrors)
    },

    highlightErrors (requiredElement) {
      // Highlight non-filled required input.
      if (this.required && (this.value === '' || this.value === null)) {
        this.highlight = true
        // Scroll to first non-filled required input.
        if (this.$el === requiredElement) {
          const elScrollOffset = this.$el.getBoundingClientRect().top
          const scrollOffset = window.pageYOffset || document.documentElement.scrollTop
          const padding = 12
          window.scrollTo({
            top: elScrollOffset + scrollOffset - padding,
            behavior: 'smooth'
          })
          window.onscroll = e => {
            const currentScrollOffset = window.pageYOffset || document.documentElement.scrollTop
            if (elScrollOffset + scrollOffset - padding === currentScrollOffset) {
              if (this.$el.tagName === 'DIV') {
                this.$el.querySelector('input').focus()
              } else {
                this.$el.focus()
              }
              window.onscroll = null
            }
          }
        }
      }
    },

    clearValue () {
      this.value = ''
    },

    removePlaceholder () {
      if (this.isTextArea && this.isPlaceholder) {
        this.value = ''
        if (this.locale === 'ar') this.setToRtlTextArea = true
      }
    },

    resetPlaceholder () {
      if (this.isTextArea && this.value === '') {
        this.value = this.defValue
        if (this.locale === 'ar') this.setToRtlTextArea = false
      }
    },

    inputChanged () {
      if (this.name === 'text_latitude' || this.name === 'text_longitude') {
        bus.$emit('geocode-lat-lon-set-manually', true)
      }
    }
  }
}
</script>

<style lang="scss">
@import "~@/assets/styles/variables";
$inputHeight: 50px;
$selectHeight: 24px;

.setting-input-wrapper{
  width: 100%;
  display: flex;

  button{
    .UI-delete{ color: #A2b4b4; }
    &:hover{
      .UI-delete{ color: white; }
    }
  }
}

.setting-input-wrapper_checkbox {
  flex: 1;
  justify-content: flex-end;
  align-items: center;
  margin-right: 20px;
  height: 50px;
}

.setting-input{
  display: block;
  width: 100%; height: $inputHeight; line-height: $inputHeight - 4;
  padding: 0 7px;
  font-family: $poppins;
  border: 1px solid transparent;
  border-radius: 5px;

  &:focus{
    border: 1px solid #757573;
    box-shadow: 0 0 5px #757573;
  }

  &.highlight{
    border: 1px solid tomato;
    box-shadow: 0 0 5px tomato;
  }

  &.rtl{
    direction: rtl;
    text-align: right;
  }
}

select.setting-input {
  line-height: $selectHeight;
}

.setting-form-item{
  display: flex;
  align-items: center;

  label[for*="text_"], label[for*="url_"], label[for*="number_"]{
    display: block;
    white-space: nowrap;
    bottom: 3px;
    flex-grow: 1;
  }
}
input[type="text"], input[type="url"], input[type="number"], textarea{
  outline: 0;
  background: transparent;
  color: white;
}

input[type="checkbox"].setting-input, select.setting-input{
  display: inline-block;
  width: auto;
  vertical-align: middle;
}
input[type="checkbox"].setting-input {
  &:before {
    height: 14px;
    width: 14px;
  }
}

input[type="number"] {
  text-align: left;
}

textarea.setting-input{
  height: 13 * $inputHeight; line-height: $f25;
  background: rgba(white, 0.05);
  border-radius: 8px;
  padding: 18px 24px 24px 24px;

  &.is-placeholder{
    color: rgba(white, 0.5);
  }
}
.setting-select-wrapper{
  position: relative;
  display: inline-block;
  .UI-expand{
    position: absolute;
    top: 50%;
    right: 0;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
  select.setting-input{
    background: darken(#364040, 1.7);
    border: 1px solid #757573;
    padding: 10px 28px 10px 18px;
    appearance: none;
    display: block;
    color: white;
    height: 100%;
    align-self: stretch;
    text-align-last: center;
    cursor: pointer;
    outline: 0;

    option{
      background: #364040;
    }
  }
}
</style>
